import merge from "lodash.merge";
import { Root, createRoot } from "react-dom/client";
import { Provider } from "react-redux";

import "./index.css";
import "./mobile-overrides.css";
import "./webchat-animations.css";
import "./webchat-position.css";

import { store } from "./store/store";
import { WebchatWidget } from "./components/WebchatWidget";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { instalarHandlersGlobais, relatarErroNaoCapturado } from "./utils/instalarHandlersGlobais";
import { sessionDataHandler } from "./sessionDataHandler";
import { initConfig } from "./store/actions/initActions";
import { ConfigState } from "./store/definitions";
import { initLogger } from "./logger";
import { BRAND, applyBrandTheme } from "./brands";
import { WIDGET_BRAND } from "./brand";
import { getBrandConfig, remoteConfigToOverrides } from "./brandConfigHandler";
import { applyLauncherPosition } from "./launcherPosition";

const defaultConfig: ConfigState = {
    serverUrl: "http://localhost:3001",
    theme: {
        isLight: true
    },
    fileAttachment: {
        enabled: true,
        maxFileSize: 16777216, // 16 MB
        // webp/heic/heif: o compartilhamento do Android converte para webp e o iPhone
        // fotografa em heic — sem eles o cliente não consegue mandar o print pedido.
        acceptedExtensions: ["jpg", "jpeg", "png", "webp", "heic", "heif", "amr", "mp3", "mp4", "pdf", "txt"]
    }
};

let root: Root | undefined;
let rootContainer: HTMLElement | undefined;

const initWebchat = async (config: ConfigState) => {
    const mergedConfig = merge({}, defaultConfig, config);
    // Aplica o tema da marca (build-time via REACT_APP_WIDGET_BRAND) antes de renderizar.
    applyBrandTheme(BRAND.theme);
    sessionDataHandler.setEndpoint(mergedConfig.serverUrl);
    store.dispatch(initConfig(mergedConfig));
    initLogger();
    // Antes de renderizar: erro que estoure durante o boot também tem que virar alerta.
    instalarHandlersGlobais();
    const rootElement = document.getElementById("twilio-webchat-widget-root");

    if (!rootElement) {
        // React 17 já estourava aqui com container nulo; mantemos a falha ALTA e explícita —
        // um widget que não monta em silêncio é pior do que um erro no console.
        throw new Error("Elemento raiz do webchat não encontrado: #twilio-webchat-widget-root");
    }

    /*
     * React 18: createRoot substitui o render() removido no 19. O root é cacheado por elemento
     * porque initWebchat pode ser chamado mais de uma vez na mesma página — criar um root novo
     * sobre um container que já tem um faz o React avisar e descartar a árvore anterior.
     */
    if (!root || rootContainer !== rootElement) {
        /*
         * 🔴 `onUncaughtError` NÃO é opcional aqui — é o que impede a pior falha de virar silêncio.
         *
         * O `ErrorBoundary` abaixo cobre o que está DENTRO dele. Erro no `Provider`, no próprio
         * boundary ou no fallback fica fora do alcance de qualquer boundary, e no React 18 esse
         * caso era re-lançado como `window.onerror` de verdade — o handler global reconhecia pelo
         * `filename` e relatava.
         *
         * O React 19 trocou esse caminho por `reportGlobalError`, e medido nos dois motores reais o
         * `filename` não vem: `""` no Chromium, `"undefined"` no WebKit. O handler global descarta
         * tudo que não casa com o diretório do bundle (`instalarHandlersGlobais.ts:138`), e com
         * razão — é o que evita relatar erro do site da marca. Resultado sem esta linha: a árvore
         * cai, o apostador fica sem widget, e a operação não fica sabendo.
         *
         * Vai pela MESMA função do handler global, então dedup, teto por sessão e impressão
         * continuam valendo: um loop de render é 1 mensagem, não N.
         */
        root = createRoot(rootElement, {
            onUncaughtError: (erro) => relatarErroNaoCapturado(erro)
        });
        rootContainer = rootElement;
    }
    /*
     * ErrorBoundary DENTRO do Provider: se a árvore quebrar, o fallback ainda monta. E ele não usa
     * Paste nem Redux de propósito — um fallback que dependa do que caiu cai junto, e o cliente
     * volta à tela branca de 2026-07-28.
     */
    root.render(
        <Provider store={store}>
            <ErrorBoundary>
                <WebchatWidget />
            </ErrorBoundary>
        </Provider>
    );

    /*
     * Override em runtime pelo dashboard (cor + logo do launcher), por marca.
     * Não bloqueia o render; se falhar/ausente, segue com os defaults de build (brands.ts).
     */
    getBrandConfig(WIDGET_BRAND)
        .then((remote) => {
            if (remote.theme) {
                applyBrandTheme({ ...BRAND.theme, ...remote.theme });
            }
            if (remote.launcherPosition) {
                applyLauncherPosition(remote.launcherPosition);
            }
            // ConfigReducer faz merge → despacha só os overrides (não perde o que já está no estado).
            const overrides = remoteConfigToOverrides(remote);
            if (Object.keys(overrides).length > 0) {
                store.dispatch(initConfig(overrides));
            }
        })
        .catch(() => undefined);
};

declare global {
    interface Window {
        Twilio: {
            initWebchat: (config: ConfigState) => void;
        };
    }
}

// Expose `initWebchat` function to window object
Object.assign(window, {
    Twilio: {
        initWebchat
    }
});
